import React, { useEffect, useState, useRef } from 'react';
import { NylasSchedulerEditor } from '@nylas/react';
import { useSelector } from 'react-redux';

import { getSchedulerInfo } from 'api/scheduler';
import { Loading } from 'components/Loading';
import { observeShadowRoots, applyStyles } from 'components/Scheduler/utils/shadowRoot';
import { CustomIdentityRequestWrapper } from './CustomIdentityWrapper';
import SchedulerPreview from './SchedulerPreview';
import SchedulerReschedule from './SchedulerReschedule';
import SchedulerCancel from './SchedulerCancel';
import { schedulerStyle, localSchedulerRootStyle } from './injected-scheduler-style';

import './scheduler-editor.scss';

const CLIENT_ID = process.env.REACT_APP_NYLAS_CLIENT_ID;

const STYLED_SHADOW_ELEMENTS = [
  'nylas-event-location',
  'nylas-event-description',
  'nylas-booking-calendar-picker',
  'nylas-calendar-picker',
  'nylas-availability-picker',
  'nylas-additional-participants',
  'nylas-participants-custom-availability',
  'nylas-form-card',
  'nylas-scheduling-method',
  'nylas-booking-confirmation-redirect',
  'nylas-buffer-time',
  'nylas-min-booking-notice',
  'nylas-limit-future-bookings',
  'nylas-min-cancellation-notice',
  'nylas-timeslot-interval',
  'radio-button-group',
  'checkbox-component',
  'nylas-booking-form-config',
  'nylas-confirmation-email',
  'input-image-url',
  'nylas-page-name',
  'nylas-reminder-emails',
  'nylas-customize-booking-settings',
  'nylas-participant-booking-calendars',
  'nylas-connected-calendars',
  'multi-select-dropdown',
  'nylas-disable-emails',
];

const UPDATABLE_SHADOW_ELEMENTS = {
  'nylas-booking-form-config': {
    nested: {
      h4: {
        textUpdates: {
          'Your name': 'Name',
          'Your Email': 'Email',
        },
      },
      'input-component': {
        nested: {
          input: {
            valueUpdates: [
              { name: '0', value: 'Name' },
              { name: '1', value: 'Email' },
            ],
          },
        },
      },
    },
  },
};


const OBSERVABLE_SHADOW_ELEMENTS = [
  'nylas-participants-custom-availability',
  'nylas-availability-picker',
  'nylas-confirmation-email',
  'nylas-connected-calendars',
  'nylas-participant-booking-calendars',
];

const SchedulerEditor = () => {
  const [redirectUrl, setRedirectUrl] = useState();
  const [logoUrl, setLogoUrl] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [nylasApiRequest, setNylasApiRequest] = useState();
  const [integrationId, setIntegrationId] = useState();
  const [user, setUser] = useState({});
  const componentRef = useRef();

  const userInfo = useSelector(state => state.global.userInfo);

  const { integrations } = userInfo || {};
  // #TODO: Need to modify this if we allow multiple integrations per account.
  // #TODO: We shouldn't need to be filtering for valid integrations here.
  // Ideally, we only have integrations that are valid, or just the exact one for this scheduler.
  const integration = integrations?.filter((_integration) => _integration.grantStatus === 'valid')[0] || null;

  // Fetches the redirect url for front end authentication for the scheduler editor.
  // Also gets the org's logo url for email confirmations.
  useEffect(() => {
    const fetch = async (_email) => {
      const response = await getSchedulerInfo(_email, `${window.location.origin}${window.location.pathname}`);
      if (response.ok) {
        const { data } = response;
        const { redirect_url, logo_url, integration_id, provider, name, email } = data || {};
        if (redirect_url) setRedirectUrl(redirect_url);
        if (logo_url) setLogoUrl(`https:${logo_url}`);
        else setLogoUrl('https://images.aisleplanner.com/org_logo_print/png/aisleplanner-rings.png');
        if (integration_id) setIntegrationId(integration_id);
        setUser({
          ... provider && { provider },
          ... name && { name },
          ... email && { email },
        });
      }
      setIsLoaded(true);
    };
    if (integration) {
      fetch(integration.email);
    }
  }, [integration]);

  useEffect(() => {
    // Custom identity request wrapper needs grant and user data to prepare the api requests
    // used in the scheduler editor component.
    if (isLoaded && integrationId) {
      setNylasApiRequest(new CustomIdentityRequestWrapper(user, redirectUrl, integrationId));
    }
    // #TODO: May need to handle an additional edge case if the scheduler info endpoint does not
    // return an integration_id.
  }, [isLoaded]);

  // Inject styles into the component.
  useEffect(() => {
    if (componentRef.current) {
      const style = document.createElement('style');
      style.textContent = schedulerStyle;
      componentRef.current.shadowRoot.append(style);
    }
  }, [componentRef.current]);

  const injectStyles = (elName, nested) => {
    const el = componentRef.current.shadowRoot.querySelector(elName);
    if (el) {
      applyStyles(el, nested, schedulerStyle);
    }
  };

  const applyUpdates = (el, updates) => {
    if (!el.shadowRoot) return;

    const applyTextUpdates = (nestedEl, textUpdates) => {
      Object.entries(textUpdates).forEach(([currentText, newText]) => {
        if (nestedEl.textContent.trim() === currentText) {
          nestedEl.textContent = newText;
        }
      });
    };

    const applyValueUpdates = (nestedEl, valueUpdates) => {
      valueUpdates.forEach(({ name, value }) => {
        if (nestedEl.name === name) {
          nestedEl.value = value;
        }
      });
    };

    const applyNestedUpdates = (parentElement, nestedUpdates) => {
      Object.entries(nestedUpdates).forEach(([nestedSelector, updateRules]) => {
        const nestedElements = parentElement.shadowRoot.querySelectorAll(nestedSelector);

        nestedElements.forEach((nestedEl) => {
          if (updateRules.textUpdates) {
            applyTextUpdates(nestedEl, updateRules.textUpdates);
          }
          if (updateRules.valueUpdates) {
            applyValueUpdates(nestedEl, updateRules.valueUpdates);
          }
          if (updateRules.nested) {
            applyNestedUpdates(nestedEl, updateRules.nested);
          }
        });
      });
    };

    if (updates.nested) {
      applyNestedUpdates(el, updates.nested);
    }
  };

  const updateLocalShadowRoots = () => {
    if (!componentRef?.current) return;

    observeShadowRoots(componentRef.current, (element) => {
      const tagName = element.tagName.toLowerCase();
      if (STYLED_SHADOW_ELEMENTS.includes(tagName)) {
        applyStyles(element, null, localSchedulerRootStyle);
      }
      const updates = UPDATABLE_SHADOW_ELEMENTS[tagName];
      if (updates) {
        applyUpdates(element, updates);
      }
    }, OBSERVABLE_SHADOW_ELEMENTS);
  };

  const scheduleShadowUpdates = () => {
    // A small delay so that the DOM has time to update.
    setTimeout(() => {
      updateLocalShadowRoots();
    }, 500);
  };

  if (!userInfo) {
    return <Loading />;
  }

  // #TODO: Need a better way to handle if a user finds themselves on this page
  // without an active integration.
  if (!integration) {
    window.location.href = '/app';
  }

  if (!isLoaded) {
    return <Loading />;
  }

  return (
    <div className="scheduler-editor">
      <NylasSchedulerEditor
        ref={componentRef}
        enableUserFeedback={false}
        nylasSessionsConfig={{
          clientId: CLIENT_ID,
          redirectUri: `${window.location.origin}/app/v2/scheduler`,
          domain: 'https://api.us.nylas.com/v3',
          hosted: true,
          accessType: 'offline',
        }}
        eventOverrides={{
          initListConfigurations: async () => {
            injectStyles('nylas-list-configurations', ['select-dropdown']);
          },
          editButtonClick: async () => {
            injectStyles('nylas-editor-tabs');
            scheduleShadowUpdates();
          },
        }}
        nylasApiRequest={nylasApiRequest}
        defaultSchedulerConfigState={{
          selectedConfiguration: {
            requires_session_auth: false,
            scheduler: {
              rescheduling_url: `${window.location.origin}/app/v2/scheduler/reschedule/:booking_ref`,
              cancellation_url: `${window.location.origin}/app/v2/scheduler/cancel/:booking_ref`,
              hide_cancellation_options: false,
              hide_rescheduling_options: false,
              ...logoUrl && {
                email_template: {
                  logo: logoUrl,
                  show_nylas_branding: false,
                },
              },
            },
          },
        }}
        schedulerPreviewLink={`${window.location.origin}/app/v2/scheduler/view/{config.id}`}
      />
    </div>
  );
};

export { SchedulerEditor, SchedulerPreview, SchedulerReschedule, SchedulerCancel };
